import { Card, IconWrapper, Select } from "@caprikorn/dynamo-ui";
import { classNames } from "@caprikorn/dynamo-utils";
import { type FC, useId } from "react";
import { Controller, useWatch } from "react-hook-form";

import { CircleCheck, Euro } from "@icons";
import { POTENCIES } from "@services/services.constants";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./TariffOptionsCard.module.css";
import type { TariffOptionsCardProps } from "./TariffOptionsCard.types";
import type { AddPodFormData } from "../AddPodForm/AddPodForm.types";
import { getIsFilled } from "../AddPodForm/AddPodForm.utils";
import formClasses from "../form.module.css";

const tariffOptionsFields: Array<keyof AddPodFormData> = ["options", "potency"];

export const TariffOptionsCard: FC<TariffOptionsCardProps> = ({
  errors,
  control,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const cardId = useId();

  const [options, potency] = useWatch({
    control,
    name: ["options", "potency"],
  });

  const isFilled = getIsFilled(tariffOptionsFields, errors, {
    options,
    potency,
  });

  return (
    <Card aria-labelledby={cardId} as="section" className={formClasses.card}>
      <h3
        className={classNames(formClasses.titleContainer, "font-title-md")}
        id={cardId}
      >
        {isFilled ? (
          <IconWrapper
            className={formClasses.successIcon}
            role="presentation"
            size="lg"
            src={CircleCheck}
          />
        ) : (
          <IconWrapper role="presentation" size="md" src={Euro} />
        )}
        {t("addPodDrawer.tariffOption.title")}
      </h3>
      <div className={classes.formContainer}>
        <div className={classes.potencyInputContainer}>
          <Controller
            control={control}
            name="options"
            render={({ field, fieldState }) => (
              <Select
                {...field}
                label={t("addPodDrawer.tariffOption.fields.option")}
                size="lg"
                value={field.value === "" ? null : field.value}
              >
                <Select.Trigger
                  className={formClasses.dropdown}
                  data-testid="TariffOptionsSelector"
                  hasError={Boolean(fieldState.error)}
                  hint={
                    fieldState.error?.message &&
                    t(fieldState.error.message, { ns: "common" })
                  }
                />
                <Select.Dropdown
                  className={formClasses.dropdown}
                  data-testid="DropDown-List"
                >
                  <Select.Options>
                    <Select.Option
                      label={t("requestDrawer.steps.potency.tariffOptionBase")}
                      value="BASE"
                    />
                    <Select.Option
                      label={t(
                        "requestDrawer.steps.potency.tariffOption2dials",
                      )}
                      value="HP/HC"
                    />
                    <Select.Option
                      label={t(
                        "requestDrawer.steps.potency.tariffOption4dials",
                      )}
                      value="HCE/HPE/HPH/HCH"
                    />
                  </Select.Options>
                </Select.Dropdown>
              </Select>
            )}
            rules={{ required: "form.error.required" }}
          />
        </div>

        <div className={classes.powerInputContainer}>
          <Controller
            control={control}
            name="potency"
            render={({ field, fieldState }) => (
              <Select
                {...field}
                label={t("addPodDrawer.tariffOption.fields.maximumPower")}
                size="lg"
                value={field.value === "" ? null : field.value}
              >
                <Select.Trigger
                  className={formClasses.dropdown}
                  data-testid="potency"
                  hasError={Boolean(fieldState.error)}
                  hint={
                    fieldState.error?.message &&
                    t(fieldState.error.message, { ns: "common" })
                  }
                />
                <Select.Dropdown
                  className={formClasses.dropdown}
                  data-testid="DropDown-List"
                >
                  <Select.Options>
                    {POTENCIES?.map((option) => (
                      <Select.Option key={option} value={option} />
                    ))}
                  </Select.Options>
                </Select.Dropdown>
              </Select>
            )}
            rules={{ required: "form.error.required" }}
          />
        </div>
      </div>
    </Card>
  );
};
